import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface BetaSignupBannerProps {
  isAuthenticated: boolean;
}

const BetaSignupBanner: React.FC<BetaSignupBannerProps> = ({ isAuthenticated }) => {
  const { pathname } = useLocation();
  const { isDemoUser } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  // Show demo credentials banner when in demo mode (on all pages)
  if (isDemoUser && !dismissed) {
    return (
      <div
        className="sticky top-0 z-[1000] bg-yellow-400 border-b border-yellow-500 px-6 py-2 shadow-md"
        data-testid="beta-signup-banner"
      >
        <div className="max-w-[1920px] mx-auto flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <span className="text-2xl" role="img" aria-label="Demo mode">🔍</span>
            <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
              <span className="text-sm font-bold text-gray-900">
                Demo Mode
              </span>
              <span className="text-sm text-gray-900">
                — You're viewing sample data for a fictional engineering team.
              </span>
            </div>
            <Link
              to="/apply"
              className="hidden sm:inline text-sm text-gray-900 underline hover:text-gray-700 font-medium ml-4"
            >
              Apply for private beta →
            </Link>
          </div>
          <button
            onClick={() => setDismissed(true)}
            className="text-gray-900 hover:text-gray-700 text-2xl leading-none font-bold w-6 h-6 flex items-center justify-center"
            aria-label="Dismiss demo banner"
            data-testid="beta-banner-dismiss"
          >
            ×
          </button>
        </div>
      </div>
    );
  }

  // Show private beta banner for non-authenticated, non-demo users (only on homepage)
  if (dismissed || pathname !== '/' || isAuthenticated) return null;

  return (
    <div
      className="bg-blue-900/40 border-b border-blue-500/30 px-6 py-3"
      data-testid="beta-signup-banner"
    >
      <div className="max-w-[1920px] mx-auto flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-blue-200">
            RetainIQ is in private beta.
          </span>
          <span className="text-sm text-blue-300 ml-1">We review every application by hand.</span>
        </div>

        <Link
          to="/apply"
          className="px-4 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-sm rounded font-medium transition-colors"
          data-testid="beta-banner-apply-link"
        >
          Apply for private beta →
        </Link>

        <button
          onClick={() => setDismissed(true)}
          className="text-blue-400 hover:text-blue-200 text-lg leading-none"
          aria-label="Dismiss banner"
          data-testid="beta-banner-dismiss"
        >
          ×
        </button>
      </div>
    </div>
  );
};

export default BetaSignupBanner;
